import { useState } from "react";
import { CELLS } from "@/constants/testIds";
import { Save, X } from "lucide-react";

const inputCls =
  "w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded focus:border-[#c8a04a] focus:outline-none";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

export default function CellForm({ cell, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: cell?.name || "",
    leader: cell?.leader || cell?.leader_name || "",
    subleader: cell?.subleader || "",
    support: cell?.support || "",
    schedule: cell?.schedule || "",
    address: cell?.address || "",
    county: cell?.county || "",
    notes: cell?.notes || "",
    active: cell ? !!cell.active : true,
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) {
      setError("El nombre de la célula es obligatorio");
      return;
    }
    setBusy(true);
    try {
      await onSave({ ...form, name: form.name.trim() });
    } catch (err) {
      setError(err?.response?.data?.detail || "Error al guardar la célula");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      data-testid={CELLS.form}
      onSubmit={submit}
      className="border border-[#d9cfb8] bg-white p-5 mb-6 max-w-2xl"
    >
      <h3 className="font-serif text-lg text-[#1c1a14] mb-4">
        {cell ? `Modificar célula — ${cell.name}` : "Nueva célula"}
      </h3>
      <div className="space-y-3">
        <Field label="Nombre de la célula *">
          <input data-testid={CELLS.nameInput} value={form.name}
            onChange={(e) => set("name", e.target.value)} className={inputCls} autoFocus />
        </Field>
        <Field label="Nombre del líder">
          <input data-testid={CELLS.leaderInput} value={form.leader}
            onChange={(e) => set("leader", e.target.value)} className={inputCls}
            placeholder="Escriba el nombre del líder" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Sublíder">
            <input data-testid={CELLS.subleaderInput} value={form.subleader}
              onChange={(e) => set("subleader", e.target.value)} className={inputCls}
              placeholder="Nombre del sublíder" />
          </Field>
          <Field label="Hermanos de apoyo">
            <input data-testid={CELLS.supportInput} value={form.support}
              onChange={(e) => set("support", e.target.value)} className={inputCls}
              placeholder="Nombres separados por coma" />
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Condado">
            <input data-testid={CELLS.countyInput} value={form.county}
              onChange={(e) => set("county", e.target.value)} className={inputCls}
              placeholder="Ej. Sector Norte" />
          </Field>
          <Field label="Días / Horario">
            <input data-testid={CELLS.scheduleInput} value={form.schedule}
              onChange={(e) => set("schedule", e.target.value)} className={inputCls}
              placeholder="Ej. Miércoles 7pm" />
          </Field>
        </div>
        <Field label="Dirección">
          <input data-testid={CELLS.addressInput} value={form.address}
            onChange={(e) => set("address", e.target.value)} className={inputCls}
            placeholder="Calle y ciudad" />
        </Field>
        <Field label="Notas">
          <textarea data-testid={CELLS.notesInput} value={form.notes} rows={2}
            onChange={(e) => set("notes", e.target.value)} className={inputCls} />
        </Field>
        <label className="inline-flex items-center gap-2 text-[13px] text-[#1c1a14]">
          <input data-testid={CELLS.activeToggle} type="checkbox" checked={form.active}
            onChange={(e) => set("active", e.target.checked)}
            className="accent-[#c8a04a] w-4 h-4" />
          Célula activa
        </label>
      </div>

      {error && (
        <div data-testid={CELLS.formError}
          className="mt-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-[12px]">
          {error}
        </div>
      )}

      <div className="mt-4 flex gap-2">
        <button data-testid={CELLS.formSave} type="submit" disabled={busy}
          className="inline-flex items-center gap-2 bg-[#1c2a3a] hover:bg-[#0f1a2c] text-[#e7c879] px-4 py-2 text-[11px] uppercase tracking-widest disabled:opacity-60">
          <Save size={13} /> {busy ? "Guardando..." : "Guardar"}
        </button>
        <button data-testid={CELLS.formCancel} type="button" onClick={onCancel} disabled={busy}
          className="inline-flex items-center gap-2 border border-[#d9cfb8] px-4 py-2 text-[11px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]">
          <X size={13} /> Cancelar
        </button>
      </div>
    </form>
  );
}
